import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import {FormService} from '@theme/components/card-components/inspection-form/form.service';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
    AssetInspectionResultDto,
    FieldType,
    FormCellType,
    FormField,
    FormViewModeType,
} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {FormFieldBaseComponent} from '@theme/components/card-components/inspection-form/cells/base/form-field-base.component';
import {BehaviorSubject, Observable} from 'rxjs';
import {S3Service} from '@core/interfaces/common/s3';
import {FormMode} from '@core/interfaces/engin/maintenance-planning/maintenance-planning';

@Component({
    selector: 'ngx-form-cell',
    templateUrl: './form-cell.component.html',
    styleUrls: ['./form-cell.component.scss'],
    providers: [FormService],
})
export class FormCellComponent<T> extends FormFieldBaseComponent<T> implements AfterViewInit, OnChanges {
    @ViewChildren('fieldElement') fieldElements: QueryList<ElementRef>;

    @Input() checkValidation: Observable<boolean> = new BehaviorSubject<boolean>(false);
    @Input() pageMode?: FormMode = FormMode.VIEW;
    @Input() viewMode: FormViewModeType = FormViewModeType.EDIT_RESULTS;
    @Input() cellType: FormCellType = FormCellType.NEW;
    @Input() fields: FormField[];
    @Input() results: AssetInspectionResultDto;
    @Output() formFieldChangeEvent = new EventEmitter();
    @Input() fieldResultForm: FormGroup;
    @Input() s3service: S3Service;
    @Input() assetFormId: number;
    @Input() selectedFieldId: number;
    @Output() onAddDeficiency = new EventEmitter<{fieldType: FieldType; fieldId: number}>();

    highlightedFieldId: number;

    FormCellType = FormCellType;
    FieldType = FieldType;
    FormMode = FormMode;

    constructor(public formService: FormService, private cd: ChangeDetectorRef) {
        super();
    }

    ngAfterViewInit() {
        this.scrollToField();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedFieldId && this.selectedFieldId) {
            this.scrollToField();
        }
    }

    private scrollToField() {
        setTimeout(() => {
            const index = this.fields?.findIndex((f) => f.id === this.selectedFieldId);
            const element = this.fieldElements?.toArray()[index];

            if (element) {
                this.highlightedFieldId = this.selectedFieldId;
                element.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
                this.cd.detectChanges();

                setTimeout(() => {
                    this.highlightedFieldId = null;
                    this.cd.detectChanges();
                }, 3000);
            }
        });
    }

    protected validate(value: T): boolean {
        throw new Error('Method not implemented.');
    }
    protected get fieldForm(): FormControl | FormArray {
        throw new Error('Method not implemented.');
    }
    protected applyValueChange(item: any): T {
        throw new Error('Method not implemented.');
    }
    protected getFormValue(): void {
        throw new Error('Method not implemented.');
    }
    protected onValidationCheck(): void {
        throw new Error('Method not implemented.');
    }

    public getHeader(): String {
        switch (this.cellType) {
            case FormCellType.NEW:
                return 'New';
            case FormCellType.CURRENT:
                return 'Current';
            case FormCellType.PREVIOUS:
                return this.results == null ? 'Previous' : 'Previous ' + this.results.completedOn;
        }
    }

    // Check if this field type is rendered, based on: field.visible and this.cellType vs. field.fieldType
    public checkRenderField(field: FormField): boolean {
        const fieldVisible = this.formService.visibleCheck(field, this.results);
        if (!fieldVisible) return false;

        /*
         * Fields which show in all cell types:
         * - Numeric
         * - Text
         * - Single select
         * - Multi select
         * - Checkbox
         * Fields which DO NOT show in PREVIOUS mode
         * - Image
         * - Video
         * - Image Video
         * - Signature
         * - Issues
         * - Table
         */

        switch (field.fieldType) {
            case FieldType.NUMERIC:
            case FieldType.TEXT:
            case FieldType.SINGLE_SELECT:
            case FieldType.MULTI_SELECT:
            case FieldType.CHECKBOX:
                return true;
            case FieldType.IMAGE:
            case FieldType.VIDEO:
            case FieldType.IMAGE_VIDEO:
            case FieldType.SIGNATURE:
            case FieldType.ISSUES:
            case FieldType.TABLE:
                return this.cellType != FormCellType.PREVIOUS;
        }
    }

    public getFieldRequired(field: FormField): boolean {
        return this.formService.checkRequiredAndViable(field, this.results);
    }
}
